/**
 * The Outbound Check (OWA-1395, src/practice/server/src/handlers/outbound_handlers.rs).
 * Every send Outlaw controls is checked for Protected Data going to someone who
 * is not an Entitled Recipient, and for Case material addressed to someone who
 * is not a contact on the Case. A flagged send is answered with 409 and an
 * `outboundCheck` warning; the Firm User confirms an Override by sending the
 * same thing again with `outboundOverride` set to its `checkId`.
 */

export type FlagReason = 'notEntitled' | 'notCaseContact';

export interface OutboundFlag {
    recipient: string;
    reason: FlagReason;
    /** Kinds of Protected Data, such as `ssn`; empty for a misdirected message. */
    kinds: string[];
}

export interface CaseContact {
    contactId: string;
    name: string;
    client: boolean;
    addresses: string[];
}

export interface OutboundWarning {
    checkId: string;
    warning: string;
    flags: OutboundFlag[];
    kinds: string[];
    caseContacts: CaseContact[];
    /** Why an earlier confirmation could not be used, when one was sent. */
    overrideRefused?: string | null;
}

/** An Automation Rule send the Outbound Check queued for its owner. */
export interface QueuedSend {
    checkId: string;
    createdAt: string;
    channel: 'email' | 'sms';
    caseId?: string | null;
    recipients: string[];
    flags: OutboundFlag[];
    kinds: string[];
    /** The Case's contacts, when a recipient is not one of them. */
    caseContacts?: CaseContact[];
    ruleId?: string | null;
    ruleName?: string | null;
    /** The stored email: its params, or just the subject when an LLM client lists it. */
    send?: {params?: {subject?: string, text?: string}, subject?: string} | null;
}

export type QueuedChoice = 'override' | 'discard';

const KIND_LABELS: Record<string, string> = {
    ssn: 'Social Security number',
    cardNumber: 'card number',
    bankAccount: 'bank account number',
    routingNumber: 'routing number',
    passport: 'passport number',
    driversLicense: "driver's license number",
    taxId: 'tax ID',
    dateOfBirth: 'date of birth',
    phi: 'health information',
    identification: 'identification number',
};

/** A kind of Protected Data in words. */
export function kindLabel(kind: string): string {
    return KIND_LABELS[kind] ?? kind;
}

/** The kinds of Protected Data in a sentence: "a Social Security number and a date of birth". */
export function kindsInWords(kinds: string[]): string {
    const words = kinds.map(kindLabel);
    if (words.length <= 1) return words.join('');
    return `${words.slice(0, -1).join(', ')} and ${words[words.length - 1]}`;
}

/** The Firm User chose not to send an email after the Outbound Check warned them. */
export class OutboundCancelledError extends Error {
    constructor() {
        super('Not sent. You chose not to send this email after the Outbound Check warning.');
        this.name = 'OutboundCancelledError';
    }
}

/** The warning in a 409 answer, if that is what the answer is. */
export function outboundWarningOf(status: number, body: unknown): OutboundWarning | undefined {
    if (status !== 409 || !body || typeof body !== 'object') return undefined;
    const warning = (body as {outboundCheck?: OutboundWarning}).outboundCheck;
    return warning && typeof warning.checkId === 'string' && Array.isArray(warning.flags) ? warning : undefined;
}

export interface OutboundApi {
    discard(checkId: string): Promise<void>;
    queued(): Promise<QueuedSend[]>;
    decideQueued(checkId: string, decision: QueuedChoice): Promise<void>;
}

type ApiResult = {status: number, body?: {message?: string, queued?: QueuedSend[]}} | undefined;

function ok(result: ApiResult, fallback: string): NonNullable<ApiResult> {
    if (!result || result.status >= 400) throw new Error(result?.body?.message || fallback);
    return result;
}

/** The real routes. `@/rest/api` is imported when first used, so tests can pass their own. */
export const serverOutboundApi: OutboundApi = {
    async discard(checkId) {
        const {makeSecureApiCall} = await import('@/rest/api');
        ok(await makeSecureApiCall(`/outbound/checks/${encodeURIComponent(checkId)}/discard`, 'POST', {}),
            'Could not record that the email was not sent');
    },
    async queued() {
        const {makeSecureApiCall} = await import('@/rest/api');
        const result = ok(await makeSecureApiCall('/outbound/queued', 'GET', {}), 'Could not load waiting sends');
        return Array.isArray(result.body?.queued) ? result.body.queued : [];
    },
    async decideQueued(checkId, decision) {
        const {makeSecureApiCall} = await import('@/rest/api');
        ok(await makeSecureApiCall(`/outbound/queued/${encodeURIComponent(checkId)}/decision`, 'POST', {decision}),
            'Could not record your decision');
    },
};
